/**
 * @title Delivery page
 * @description The page shown when the consumer selects how to send in the items
 * @image https://storageaccountourimbea2.blob.core.windows.net/tradein2guide/dropoff.png
 */
import {useNavigate} from "react-router-dom";
import {useRecoilState, useRecoilValue} from "recoil";
import {ReturnsCartAtom} from "../returns/ReturnsCart";
import {AccessContextData} from "../../../providers/access/AppContextProvider";
import * as React from "react";
import {useEffect} from "react";
import {PrivateRenderer} from "../PrivateRenderer";
import {StyledDiv, StyledTypography} from "../../../providers/layout/LayoutProvider";
import {makeStyles} from "@mui/styles";
import {
   ConsumerCarrierFragment,
   EnumTradeInPayoutType,
   ReturnsPageConsumerDataDocument,
   useConsumerCarriersQuery,
   useTradeInCreateMutation
} from "../../../Queries";
import {FreightBox} from "./FreightBox";
import {isPreview} from "../PreviewContext";
import {LocalizationsAtom} from "../../../providers/localization/LocalizationProvider";
import {ReturnsPagePath} from "../returns/ReturnsPage";
import {TradeInPagePath} from "../tradein/TradeInPage";
import {Analytics, AnalyticsEvent} from "../../../analytics/Analytics";
import {Dialogs} from "../../../Dialog";
import {Progress} from "../../../components/Progress";

export const DeliveryPagePath = () => {
   return ReturnsPagePath() + "/delivery";
}

export const DeliveryPage = () => {
   const localizations = useRecoilValue(LocalizationsAtom);
   const [cart, setCart] = useRecoilState(ReturnsCartAtom);
   const appContext = useRecoilValue(AccessContextData);
   const [consumerCarrier, setConsumerCarrier] = React.useState<ConsumerCarrierFragment | null>(null);
   const classes = useStyles();
   const navigate = useNavigate();
   const [createTradeIn] = useTradeInCreateMutation();

   const {data} = useConsumerCarriersQuery({
      variables: {
         shopId: appContext.shopId,
         consumerId: appContext.consumerId!
      }
   })

   const onNextPath = async () => {
      const potentialIds = cart.potentials.map(item => {
         return item._id
      });
      const tradeInId = await createTradeIn({
         variables: {
            consumerId: appContext.consumerId!,
            potentialIds: potentialIds,
            brandCarrierId: consumerCarrier?._id || data?.ConsumerCarriers?.find(carrier => true)?._id,
            payout: cart.payoutType,
            comments: cart.comments.map(comment => {
               return {
                  potentialId: comment.potentialId,
                  comment: comment.comment
               }
            })
         },
         refetchQueries: [{
            query: ReturnsPageConsumerDataDocument,
            variables: {
               preview: isPreview()
            }
         }],
         awaitRefetchQueries: true
      });
      if ((tradeInId.errors || []).length > 0) {
         if((tradeInId.errors||[]).find(err => err.message.includes('Exceeded the maximum voucher'))) {
            Analytics.event(AnalyticsEvent.trackError, {
               page: 'DeliveryPage',
               message: 'Maximum reward value exceeded',
            });
            Dialogs.info({
               title: localizations.tradeInMaximumTitle,
               subtitle: localizations.tradeInMaximumDesc,
            })
         } else {
            Analytics.event(AnalyticsEvent.trackError, {
               page: 'DeliveryPage',
               message: 'Unexpected error occured',
               details: JSON.stringify(tradeInId.errors, null, 2)
            });
         }
         return ReturnsPagePath()
      } else {
         setCart({
            potentials: [],
            version: cart.version + 1,
            payoutType: EnumTradeInPayoutType.Voucher,
            comments: []
         });

         Analytics.event(AnalyticsEvent.trackCompletion, {
            tradeInCarrier: data?.ConsumerCarriers?.find(carrier => true)?.shippingCarrier?.name || 'Unknown'
         });
         return TradeInPagePath(tradeInId.data!.TradeInCreate!._id)
      }

   }

   useEffect(() => {
      if (cart === null || cart.potentials.length === 0) {
         navigate(ReturnsPagePath());
      }
      if (appContext.pos) {
         (async function () {
            navigate(await onNextPath());
         })();
      }
      // eslint-disable-next-line
   }, [appContext.pos, cart, navigate])

   useEffect(() => {
      if (data) {
         if (data?.ConsumerCarriers?.length === 1) {
            (async function () {
               let s = await onNextPath();
               navigate(s);
            })();
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data])

   if (!data || appContext.pos || data?.ConsumerCarriers?.length === 1) {
      return <Progress/>
   }

   const onSelect = (type: ConsumerCarrierFragment) => {
      setConsumerCarrier(type);
   }


   // Do not remove these lines, used by the prepare script
   //localizations.carrierName_shipmondo
   //localizations.carrierDescription_shipmondo
   //localizations.carrierName_pickup
   //localizations.carrierDescription_pickup
   //localizations.carrierName_dao
   //localizations.carrierDescription_dao
   //localizations.carrierName_gls
   //localizations.carrierDescription_gls
   //localizations.carrierName_b2c
   //localizations.carrierDescription_b2c
   //localizations.carrierName_dhl
   //localizations.carrierDescription_dhl

   return <PrivateRenderer step={"shipping"} onNextPath={onNextPath} swirlOnSubmit={true}>
      <StyledDiv type={"main"} className={classes.page}>
         <div className={classes.pageTitle}>
            <StyledTypography variant={"h1"}>
               {localizations.shippingTitle}
            </StyledTypography>
            <StyledTypography variant={"body1"}>
               {localizations.shippingDescription}
            </StyledTypography>
         </div>
         <div className={classes.shippingContainer}>
            {data?.ConsumerCarriers?.map(carrier => {
               let localizationKey = carrier!.shippingCarrier?.localizationKey || carrier!.shippingCarrier?.carrierId;
               return <FreightBox id={carrier?._id}
                                  onClick={() => onSelect(carrier!)}
                                  image={carrier!.shippingCarrier?.logo || 'https://storageaccountourimbea2.blob.core.windows.net/web/packit.svg'}
                                  title={localizations.getString('carrierName_' + localizationKey)}
                                  description={localizations.getString('carrierDescription_' + localizationKey)}
                                  selected={consumerCarrier === carrier}/>
            })}
            {!data?.ConsumerCarriers?.length && <>{localizations.consumerFreightNoService}</>}
         </div>
      </StyledDiv>
   </PrivateRenderer>
}


const useStyles = makeStyles({
   page: {
      display: 'flex',
      width: '100%',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'start',
   },
   shippingContainer: {
      width: '600px',
      display: 'flex',
      gap: '20px',
      flexDirection: 'column'
   },
   pageTitle: {
      width: '440px',
      textAlign: 'center',
      marginBottom: '40px'
   },
   '@media only screen and (max-width: 800px)': {
      pageTitle: {
         width: '90%',
         maxWidth: '440px'
      },
      shippingContainer: {
         width: '80%'
      }
   },

});
